import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';

function buildListOrderQuery(isoStart, isoEnd, page = 1, limit = 50) {
  return `
{
  listOrder(
    orderedAt: { gte: "${isoStart}", lt: "${isoEnd}" }
    pagination: { page: ${page}, limit: ${limit} }
  ) {
    data {
      id
      orderNumber
      totalPrice
      orderedAt
      salesChannel { id name }
      customer { id }
      orderLineItems { id quantity price finalPrice }
      orderLineItems {
        id
        quantity
        price
        finalPrice
        variant {
          id
          productId
          name
          sku
          slug
          brand { id name }
        }
      }
    }
  }
}
`.trim();
}

function buildListCustomerQuery(page = 1, limit = 300) {
  return `
{
  listCustomer(
    pagination: { page: ${page}, limit: ${limit} }
  ) {
    data {
      id
      orderCount
      totalOrderPrice
      firstOrderDate
      lastOrderDate
      accountStatus
    }
  }
}
`.trim();
}

const formatCurrency = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const formatDateTime = (v) => {
  if (!v) return '-';
  // v can be ISO string or unix ms
  try {
    let d;
    if (typeof v === 'number' || (typeof v === 'string' && /^\d+$/.test(v))) {
      d = new Date(Number(v));
    } else {
      d = new Date(v);
    }
    if (Number.isNaN(d.getTime())) return String(v);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
  } catch {
    return String(v);
  }
};

// Lightweight responsive line chart using SVG (no external deps)
function MiniLineChart({ data, height = 220, color = '#0ea5e9' }) {
  if (!Array.isArray(data) || data.length === 0) {
    return <div className="text-sm text-gray-500">Grafik için veri yok.</div>;
  }
  const padding = { top: 10, right: 10, bottom: 24, left: 10 };
  const innerH = height - padding.top - padding.bottom;
  const step = Math.max(26, Math.floor(760 / Math.max(1, data.length - 1)));
  const width = padding.left + padding.right + step * Math.max(1, data.length - 1);
  const values = data.map(d => d.value);
  const max = Math.max(1, ...values);
  const min = 0;
  const yScale = (v) => {
    const t = (v - min) / (max - min || 1);
    return padding.top + (1 - t) * innerH;
  };
  const xScale = (i) => padding.left + i * step;
  const path = data
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(d.value)}`)
    .join(' ');
  const area = `${path} L ${xScale(data.length - 1)} ${yScale(min)} L ${xScale(0)} ${yScale(min)} Z`;
  // choose a few bottom ticks
  const tickCount = Math.min(6, data.length);
  const tickIdx = new Set(
    Array.from({ length: tickCount }, (_, i) => Math.round((i * (data.length - 1)) / (tickCount - 1)))
  );
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id="gradArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* baseline */}
      <line
        x1={padding.left}
        y1={yScale(0)}
        x2={width - padding.right}
        y2={yScale(0)}
        stroke="#e5e7eb"
      />
      {/* area */}
      <path d={area} fill="url(#gradArea)" />
      {/* line */}
      <path d={path} fill="none" stroke={color} strokeWidth="2.5" />
      {/* points */}
      {data.map((d, i) => (
        <circle key={i} cx={xScale(i)} cy={yScale(d.value)} r="2.2" fill={color} />
      ))}
      {/* bottom ticks */}
      {data.map((d, i) =>
        tickIdx.has(i) ? (
          <g key={`t-${i}`}>
            <line
              x1={xScale(i)}
              y1={height - padding.bottom}
              x2={xScale(i)}
              y2={height - padding.bottom + 4}
              stroke="#9ca3af"
            />
            <text
              x={xScale(i)}
              y={height - 4}
              fontSize="10"
              textAnchor="middle"
              fill="#6b7280"
            >
              {d.label.slice(5)}{/* show MM-DD */}
            </text>
          </g>
        ) : null
      )}
    </svg>
  );
}

export default function IkasOrdersViewer() {
  const [storeName, setStoreName] = useState('petzzshop');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [pageLimit, setPageLimit] = useState(50);
  const [maxPages, setMaxPages] = useState(20);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;

  const isoStart = useMemo(() => `${startDate}T00:00:00.000Z`, [startDate]);
  const isoEnd = useMemo(() => `${endDate}T23:59:59.999Z`, [endDate]);

  const flattenedItems = useMemo(() => {
    const rows = [];
    for (const o of orders) {
      const orderNumber = o?.orderNumber;
      const orderedAt = o?.orderedAt;
      const customerId = o?.customer?.id || '-';
      const salesChannelName = o?.salesChannel?.name || '-';
      const items = Array.isArray(o?.orderLineItems) ? o.orderLineItems : [];
      for (const it of items) {
        rows.push({
          key: `${o?.id || 'o'}-${it?.id || 'i'}`,
          orderNumber,
          orderedAt,
          customerId,
          salesChannelName,
          quantity: Number(it?.quantity || 0),
          price: Number(it?.price || 0),
          finalPrice: Number(it?.finalPrice || 0),
          productName: it?.variant?.name || '-',
          sku: it?.variant?.sku || '-',
          lineItemId: it?.id || '-',
          productId: it?.variant?.productId || '-',
          brandName: it?.variant?.brand?.name || '-'
        });
      }
    }
    return rows;
  }, [orders]);

  // Daily aggregates (finalPrice sum, items, orders)
  const daily = useMemo(() => {
    const map = new Map();
    const orderSetByDay = new Map();
    for (const o of orders) {
      const d = new Date(o?.orderedAt ? Number(o.orderedAt) : Date.now());
      const key = new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().slice(0, 10);
      const items = Array.isArray(o?.orderLineItems) ? o.orderLineItems : [];
      const itemsSum = items.reduce((acc, it) => acc + Number(it?.quantity || 0), 0);
      const rev = items.reduce((acc, it) => acc + Number(it?.finalPrice || 0), 0);
      const prev = map.get(key) || { revenue: 0, items: 0 };
      map.set(key, { revenue: prev.revenue + rev, items: prev.items + itemsSum });
      const s = orderSetByDay.get(key) || new Set();
      s.add(o.orderNumber || o.id);
      orderSetByDay.set(key, s);
    }
    const out = Array.from(map.entries())
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([label, v]) => ({
        label,
        revenue: v.revenue,
        items: v.items,
        orders: (orderSetByDay.get(label) || new Set()).size
      }));
    return out;
  }, [orders]);

  // Sales channel breakdown
  const byChannel = useMemo(() => {
    const map = new Map();
    for (const o of orders) {
      const channel = o?.salesChannel?.name || 'Unknown';
      const items = Array.isArray(o?.orderLineItems) ? o.orderLineItems : [];
      const rev = items.reduce((acc, it) => acc + Number(it?.finalPrice || 0), 0);
      const qty = items.reduce((acc, it) => acc + Number(it?.quantity || 0), 0);
      const prev = map.get(channel) || { revenue: 0, items: 0, orders: 0 };
      map.set(channel, { revenue: prev.revenue + rev, items: prev.items + qty, orders: prev.orders + 1 });
    }
    return Array.from(map.entries())
      .map(([channel, v]) => ({ channel, ...v }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [orders]);

  const [metric, setMetric] = useState('revenue'); // 'revenue' | 'orders' | 'items'
  const lineData = useMemo(() => {
    const key = metric;
    return daily.map(d => ({ label: d.label, value: d[key] || 0 }));
  }, [daily, metric]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    setOrders([]);
    setMeta(null);
    try {
      if (!storeName || !clientId || !clientSecret) {
        throw new Error('Store, Client ID ve Client Secret gerekli');
      }
      const collected = [];
      let page = 1;
      let lastMeta = null;
      for (; page <= Number(maxPages || 1); page += 1) {
        const query = buildListOrderQuery(isoStart, isoEnd, page, Number(pageLimit || 50));
        const res = await fetch('/api/labs/ikas/orders', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(brandId ? { 'X-Brand-ID': brandId } : {})
          },
          credentials: 'include',
          body: JSON.stringify({
            storeName,
            clientId,
            clientSecret,
            query
          })
        });
        const js = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(js?.error || js?.details || `İstek başarısız: ${res.status}`);
        }
        lastMeta = js?.meta || null;
        const pageOrders = js?.payload?.data?.listOrder?.data || [];
        if (!Array.isArray(pageOrders) || pageOrders.length === 0) {
          break;
        }
        collected.push(...pageOrders);
        await new Promise(r => setTimeout(r, 150));
      }
      setOrders(collected);
      setMeta(lastMeta);
      if (collected.length === 0) {
        toast('Bu aralıkta sipariş bulunamadı');
      } else {
        toast.success(`Toplam ${collected.length} sipariş alındı`);
      }
    } catch (e) {
      console.error('[IkasOrdersViewer] error', e);
      setError(e?.message || 'İstek başarısız');
      toast.error(e?.message || 'İstek başarısız');
    } finally {
      setLoading(false);
    }
  }, [storeName, clientId, clientSecret, isoStart, isoEnd, pageLimit, maxPages, brandId]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">İkas Overview</h1>
          <p className="text-sm text-gray-500 mt-1">Günlük trend grafiği ve satış kanalı kırılımıyla sipariş özeti.</p>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white border rounded-xl p-4 space-y-4">
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Store Name</label>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="petzzshop"
              value={storeName}
              onChange={(e) => setStoreName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Client ID</label>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Client Secret</label>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
            />
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Başlangıç</label>
            <input
              type="date"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Bitiş</label>
            <input
              type="date"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Sayfa Limit</label>
            <input
              type="number"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={pageLimit}
              onChange={(e) => setPageLimit(Number(e.target.value || 50))}
              min={1}
              max={200}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Maks. Sayfa</label>
            <input
              type="number"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={maxPages}
              onChange={(e) => setMaxPages(Number(e.target.value || 20))}
              min={1}
              max={100}
            />
          </div>
        </div>

        <div className="flex gap-3">
          <button
            className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${loading ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}
            onClick={fetchOrders}
            disabled={loading}
          >
            {loading ? 'Çekiliyor…' : 'Siparişleri Çek'}
          </button>
          {error && <div className="text-sm text-red-600 self-center">{error}</div>}
        </div>
      </div>

      {/* Daily Chart + KPIs */}
      <div className="bg-white border rounded-xl p-4">
        <div className="grid md:grid-cols-4 gap-4 mb-4">
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
            <div className="text-xs text-emerald-700">Toplam Gelir</div>
            <div className="text-xl font-semibold text-emerald-800 mt-1">
              {formatCurrency(daily.reduce((a, b) => a + b.revenue, 0))} TL
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
            <div className="text-xs text-blue-700">Toplam Sipariş</div>
            <div className="text-xl font-semibold text-blue-800 mt-1">
              {daily.reduce((a, b) => a + b.orders, 0)}
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
            <div className="text-xs text-amber-700">Toplam Ürün Adedi</div>
            <div className="text-xl font-semibold text-amber-800 mt-1">
              {daily.reduce((a, b) => a + b.items, 0)}
            </div>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
            <div className="text-xs text-gray-600">Gün Sayısı</div>
            <div className="text-xl font-semibold text-gray-800 mt-1">{daily.length}</div>
          </div>
        </div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-gray-700">
            Günlük Trend
          </div>
          <div className="flex gap-2">
            <button
              className={`px-2.5 py-1.5 rounded-lg text-xs border ${metric === 'revenue' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`}
              onClick={() => setMetric('revenue')}
            >
              Gelir
            </button>
            <button
              className={`px-2.5 py-1.5 rounded-lg text-xs border ${metric === 'orders' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`}
              onClick={() => setMetric('orders')}
            >
              Sipariş
            </button>
            <button
              className={`px-2.5 py-1.5 rounded-lg text-xs border ${metric === 'items' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'}`}
              onClick={() => setMetric('items')}
            >
              Ürün Adedi
            </button>
          </div>
        </div>
        <div className="w-full">
          <MiniLineChart data={lineData} height={220} color={metric === 'revenue' ? '#059669' : metric === 'orders' ? '#2563eb' : '#d97706'} />
        </div>
      </div>

      {/* Sales Channel Breakdown */}
      <div className="bg-white border rounded-xl p-4">
        <h2 className="text-lg font-semibold mb-3">Satış Kanalı Kırılımı</h2>
        {byChannel.length === 0 ? (
          <div className="text-sm text-gray-500">Kayıt yok.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">Satış Kanalı</th>
                  <th className="py-2 pr-4">Sipariş</th>
                  <th className="py-2 pr-4">Ürün Adedi</th>
                  <th className="py-2 pr-4">Gelir</th>
                </tr>
              </thead>
              <tbody>
                {byChannel.map((r) => (
                  <tr key={r.channel} className="border-b hover:bg-gray-50">
                    <td className="py-2 pr-4 font-medium">{r.channel}</td>
                    <td className="py-2 pr-4">{r.orders}</td>
                    <td className="py-2 pr-4">{r.items}</td>
                    <td className="py-2 pr-4">{formatCurrency(r.revenue)} TL</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Purchased items table */}
      <div className="bg-white border rounded-xl p-4">
        <h2 className="text-lg font-semibold mb-3">Satın Alınan Ürünler</h2>
        {loading ? (
          <div className="text-sm text-gray-500">Yükleniyor…</div>
        ) : flattenedItems.length === 0 ? (
          <div className="text-sm text-gray-500">Kayıt yok.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">Order #</th>
                  <th className="py-2 pr-4">Tarih</th>
                  <th className="py-2 pr-4">Customer ID</th>
                  <th className="py-2 pr-4">Satış Kanalı</th>
                  <th className="py-2 pr-4">Ürün</th>
                  <th className="py-2 pr-4">SKU</th>
                  <th className="py-2 pr-4">Item ID</th>
                  <th className="py-2 pr-4">Adet</th>
                  <th className="py-2 pr-4">Fiyat</th>
                  <th className="py-2 pr-4">Final Fiyat</th>
                  <th className="py-2 pr-4">Marka</th>
                </tr>
              </thead>
              <tbody>
                {flattenedItems.map((r) => (
                  <tr key={r.key} className="border-b hover:bg-gray-50">
                    <td className="py-2 pr-4 font-medium">{r.orderNumber}</td>
                    <td className="py-2 pr-4">{formatDateTime(r.orderedAt)}</td>
                    <td className="py-2 pr-4">{r.customerId}</td>
                    <td className="py-2 pr-4">{r.salesChannelName}</td>
                    <td className="py-2 pr-4">{r.productName}</td>
                    <td className="py-2 pr-4">{r.sku}</td>
                    <td className="py-2 pr-4 font-mono">{r.lineItemId}</td>
                    <td className="py-2 pr-4">{r.quantity}</td>
                    <td className="py-2 pr-4">{formatCurrency(r.price)} TL</td>
                    <td className="py-2 pr-4">{formatCurrency(r.finalPrice)} TL</td>
                    <td className="py-2 pr-4">{r.brandName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


